import {
  Component,
  computed,
  effect,
  input,
  signal,
  viewChild,
  inject,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { getOffsetDate, startOfDay } from '@app/core/util/date_utils';
import {
  AlertFilterValue,
  IAlertFilterOption,
  IAlertBulkActionOption,
  AlertBulkActionValue,
  IAlertTestItem,
} from '@app/account/notifications/interfaces/notifications-data.interfaces';

@Component({
  selector: 'app-alerts-menu',
  templateUrl: './alerts-menu.component.html',
  standalone: false,
})
export class AlertsMenuComponent {
  private _router: Router = inject(Router);

  // input signals
  layout = input<'menu' | 'standalone'>('standalone');
  alerts = input<IAlertTestItem[]>([]);

  // output signals
  alertsCountChange = output<number>();
  markAllAsRead = output<void>();
  archiveAllRead = output<void>();
  archiveAll = output<void>();

  toggleMarkAsRead = output<IAlertTestItem>();
  archiveAlert = output<IAlertTestItem>();
  alertClick = output<IAlertTestItem>();

  alertPopover = viewChild<Popover>('alertsPopover');
  alertFilterPopover = viewChild<Popover>('alertFilterPopover');
  alertBulkActionsPopover = viewChild<Popover>('alertBulkActionsPopover');

  alertFilterPopoverVisible = signal<boolean>(false);
  alertBulkActionsPopoverVisible = signal<boolean>(false);
  alertsPopoverVisible = signal<boolean>(false);
  drawerVisible = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  alertsCount = computed(() => this.alerts().length);
  activeAlertsCount = computed(
    () =>
      this.alerts().filter((alert: IAlertTestItem) => !alert?.read_at).length,
  );

  alertsTitle = computed(() => {
    return this.selectedFilter()?.value === AlertFilterValue.Archived
      ? 'Archived Alerts'
      : 'Critical Alerts';
  });

  isMenuLayout = computed(() => this.layout() === 'menu');
  isStandaloneLayout = computed(() => this.layout() === 'standalone');
  isSidepanelLayout = computed(
    () => this.isStandaloneLayout() && this.alertsCount() >= 6,
  );

  filteredAlerts = computed(() => {
    const list: IAlertTestItem[] = this.alerts();

    const filter: AlertFilterValue = this.selectedFilter()?.value;

    if (!filter) {
      return list.filter((a: IAlertTestItem) => !a.archived_at);
    }

    switch (filter) {
      case AlertFilterValue.UnreadOnly:
        return list.filter((a: IAlertTestItem) => !a.archived_at && !a.read_at);
      case AlertFilterValue.Archived:
        return list.filter((a: IAlertTestItem) => !!a.archived_at);
      default:
        return list.filter((a: IAlertTestItem) => !a.archived_at);
    }
  });

  filteredAlertsCount = computed(() => this.filteredAlerts().length);

  alertsToday = computed(() => {
    const startOfToday: Date = startOfDay(new Date());
    return this.filteredAlerts().filter(
      (alert) => alert.sent_at >= startOfToday,
    );
  });

  alertsYesterday = computed(() => {
    const startOfToday: Date = startOfDay(new Date());
    const startOfYesterday: Date = startOfDay(getOffsetDate(1));
    return this.filteredAlerts().filter(
      (alert) =>
        alert.sent_at >= startOfYesterday && alert.sent_at < startOfToday,
    );
  });

  alertsOlder = computed(() => {
    const startOfYesterday: Date = startOfDay(getOffsetDate(1));
    return this.filteredAlerts().filter(
      (alert) => alert.sent_at < startOfYesterday,
    );
  });

  showFilterIndicator = computed(() => {
    const value = this.selectedFilter()?.value;
    return (
      value === AlertFilterValue.UnreadOnly ||
      value === AlertFilterValue.Archived
    );
  });

  filterOptions: IAlertFilterOption[] = [
    {
      label: 'Unread & read',
      value: AlertFilterValue.UnreadAndRead,
      iconUrl: 'assets/icons/general/action-list.svg',
    },
    {
      label: 'Unread only',
      value: AlertFilterValue.UnreadOnly,
      iconUrl: 'assets/icons/general/communication-unread.svg',
    },
    {
      label: 'Archived',
      value: AlertFilterValue.Archived,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  bulkActionOptions: IAlertBulkActionOption[] = [
    {
      label: 'Mark all as read',
      value: AlertBulkActionValue.MarkAllRead,
      iconUrl: 'assets/icons/general/system-feedback-success.svg',
    },
    {
      label: 'Archive all',
      value: AlertBulkActionValue.ArchiveAll,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
    {
      label: 'Archive all read',
      value: AlertBulkActionValue.ArchiveAllRead,
      iconUrl: 'assets/icons/general/action-archive.svg',
    },
  ];

  selectedFilter = signal<IAlertFilterOption | null>(this.filterOptions[0]);
  selectedBulkAction = signal<IAlertBulkActionOption | null>(null);

  constructor() {
    effect(() => {
      this.alertsCountChange.emit(this.alertsCount());
    });

    effect(() => {
      if (!this.drawerVisible()) {
        this.selectedFilter.set(this.filterOptions[0]);
      }
    });
  }

  onStandaloneButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.isSidepanelLayout()) {
      this.drawerVisible.update((visible: boolean) => !visible);
      this.alertPopover()?.hide();
    } else {
      this.alertPopover().toggle(event);
      this.drawerVisible.set(false);
    }
  }

  onAlertsPopoverShow(): void {
    this.alertsPopoverVisible.set(true);
  }

  onAlertsPopoverHide(): void {
    this.alertsPopoverVisible.set(false);
    // Reset the selected filter when the alerts popover is hidden
    this.selectedFilter.set(this.filterOptions[0]);
  }

  onAlertFilterPopoverShow(): void {
    this.alertFilterPopoverVisible.set(true);
  }

  onAlertFilterPopoverHide(): void {
    this.alertFilterPopoverVisible.set(false);
  }

  onAlertBulkActionsPopoverShow(): void {
    this.alertBulkActionsPopoverVisible.set(true);
  }

  onAlertBulkActionsPopoverHide(): void {
    this.alertBulkActionsPopoverVisible.set(false);
  }

  onFilterButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.alertBulkActionsPopoverVisible()) {
      this.alertBulkActionsPopover().hide();
    }

    this.alertFilterPopover().toggle(event);
  }

  onFilterSelectionChange(selectedFilter: IAlertFilterOption | null): void {
    if (!selectedFilter) return;

    this.selectedFilter.set(selectedFilter);
    this.alertFilterPopover()?.hide();
  }

  onBulkActionsButtonClick(event: MouseEvent): void {
    if (!event) return;

    if (this.alertFilterPopoverVisible()) {
      this.alertFilterPopover().hide();
    }

    this.alertBulkActionsPopover()?.toggle(event);
  }

  onBulkActionSelectionChange(selected: IAlertBulkActionOption | null): void {
    const value: AlertBulkActionValue | undefined = selected?.value;
    if (!value) return;

    switch (value) {
      case AlertBulkActionValue.MarkAllRead:
        this.onMarkAllAsRead();
        break;
      case AlertBulkActionValue.ArchiveAllRead:
        this.onArchiveAllRead();
        break;
      case AlertBulkActionValue.ArchiveAll:
        this.onArchiveAll();
        break;
    }

    this.selectedBulkAction.set(null);
    this.alertBulkActionsPopover()?.hide();
  }

  onMarkAllAsRead(): void {
    this.markAllAsRead.emit();
  }

  onArchiveAllRead(): void {
    this.archiveAllRead.emit();
  }

  onArchiveAll(): void {
    this.archiveAll.emit();
  }

  openSettings(): void {
    this._router.navigate([
      '/account/notification-preferences/general-preferences',
    ]);

    this._hideView();
  }

  onToggleMarkAsRead(alert: IAlertTestItem): void {
    this.toggleMarkAsRead.emit(alert);
  }

  onAlertArchive(alert: IAlertTestItem): void {
    this.archiveAlert.emit(alert);
  }

  onAlertClick(alert: IAlertTestItem): void {
    if (!alert) {
      return;
    }

    this._hideView();
    this.alertClick.emit(alert);
  }

  private _hideView(): void {
    this.alertPopover()?.hide();
    this.alertFilterPopover()?.hide();
    this.alertBulkActionsPopover()?.hide();

    this.drawerVisible.set(false);
  }
}
